import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { apiGet } from "../../api";
import { toast } from "react-toastify";

const StatCard = ({ label, value }) => (
  <div className="rounded-lg border border-border/70 bg-card p-5">
    <p className="text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-1">
      {label}
    </p>
    <p className="font-mono text-xl text-foreground break-words">{value ?? 0}</p>
  </div>
);

const thCls = "border-b border-border/70 px-5 py-3 text-[10px] font-medium uppercase tracking-[1.5px] text-[#9aa1ac] text-left";
const tdCls = "border-b border-border/40 px-5 py-3.5 text-sm text-foreground";

const UserCreditStats = () => {
  const { username: paramUsername } = useParams();
  const navigate = useNavigate();
  const [searchInput, setSearchInput] = useState(paramUsername || "");
  const [username, setUsername] = useState(paramUsername || "");
  const [appCredit, setAppCredit] = useState(null);
  const [referralCredit, setReferralCredit] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [network, setNetwork] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchAll = useCallback(async (u) => {
    if (!u) return;
    setLoading(true);
    setAppCredit(null);
    setReferralCredit(null);
    setSubscription(null);
    setNetwork(null);
    try {
      const [r1, r2, r3, r4] = await Promise.allSettled([
        apiGet(`/api/admin/user/${u}/app-credit-stats`),
        apiGet(`/api/admin/user/${u}/referral-credit-stats`),
        apiGet(`/api/admin/user/${u}/referral-subscription-stats`),
        apiGet(`/api/admin/user/${u}/referral-network-stats`),
      ]);
      if (r1.status === "fulfilled" && r1.value.data.success) setAppCredit(r1.value.data.data);
      if (r2.status === "fulfilled" && r2.value.data.success) setReferralCredit(r2.value.data.data);
      if (r3.status === "fulfilled" && r3.value.data.success) setSubscription(r3.value.data.data);
      if (r4.status === "fulfilled" && r4.value.data.success) setNetwork(r4.value.data.data);
      const anyFailed = [r1, r2, r3, r4].some((r) => r.status === "rejected");
      if (anyFailed) toast.warn("Some stats failed to load");
    } catch (err) {
      console.error(err);
      toast.error("Failed to fetch user credit stats");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (paramUsername) {
      setSearchInput(paramUsername);
      setUsername(paramUsername);
      fetchAll(paramUsername);
    }
  }, [paramUsername, fetchAll]);

  const handleSearch = (e) => {
    e.preventDefault();
    const u = searchInput.trim();
    if (!u) {
      toast.error("Please enter a username");
      return;
    }
    if (u !== paramUsername) {
      navigate(`/credit-stats/user/${u}`);
    } else {
      setUsername(u);
      fetchAll(u);
    }
  };

  return (
    <div className="p-4">
      <div className="flex items-center gap-3 text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground mb-1">
        <span className="h-px w-6 bg-ginox-green" aria-hidden="true" />
        Community
      </div>
      <h1 className="font-serif italic text-[45px] leading-[1.02] text-foreground">
        User Credit <span className="text-gradient-ginox">Stats</span>
        {username && (
          <span className="text-lg font-normal text-muted-foreground not-italic"> — {username}</span>
        )}
      </h1>
      <p className="text-sm text-muted-foreground mb-6">
        View credit balances and referral network stats for any user.
      </p>

      <form onSubmit={handleSearch} className="mb-6 flex gap-2 max-w-md">
        <input
          type="text"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder="Enter username"
          className="flex-1 rounded-md border border-border/70 bg-white/[0.03] px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/50 focus:border-ginox-green/50 focus:outline-none focus:ring-1 focus:ring-ginox-green/30"
        />
        <button
          type="submit"
          className="rounded-md border border-ginox-green/60 bg-ginox-green/10 px-4 py-2 text-xs font-medium text-ginox-green hover:bg-ginox-green/20 transition-colors"
        >
          Search
        </button>
      </form>

      {loading && (
        <div className="text-center text-sm text-muted-foreground py-12">Loading stats...</div>
      )}

      {!loading && !username && (
        <div className="text-center text-sm text-muted-foreground py-12">
          Enter a username to view credit stats
        </div>
      )}

      {!loading && username && (
        <>
          {appCredit && (
            <div className="mb-6">
              <p className="text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-3">
                App Credit
              </p>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                <StatCard label="Available Balance" value={appCredit.availableBalance} />
                <StatCard label="Total Purchased" value={appCredit.totalPurchased} />
                <StatCard label="Total Spent" value={appCredit.totalSpent} />
                <StatCard label="Signal Channel" value={appCredit.spentOnSignalChannel} />
                <StatCard label="IPVPN" value={appCredit.spentOnIPVPN} />
                <StatCard label="Ginox AI" value={appCredit.spentOnGinoxAi} />
              </div>
            </div>
          )}

          {referralCredit && (
            <div className="mb-6">
              <p className="text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-3">
                Referral Credit
              </p>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                <StatCard label="Credits Available" value={referralCredit.totalCreditsAvailable} />
                <StatCard label="Credits Used" value={referralCredit.totalCreditsUsed} />
                <StatCard label="Signal Channel" value={referralCredit.spentOnSignalChannel} />
                <StatCard label="IPVPN" value={referralCredit.spentOnIPVPN} />
                <StatCard label="Ginox AI" value={referralCredit.spentOnGinoxAi} />
              </div>
            </div>
          )}

          {subscription && (
            <div className="mb-6">
              <p className="text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-3">
                Referral Subscriptions
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <StatCard label="Total Subscriptions" value={subscription.totalSubscriptions} />
                <StatCard label="Active" value={subscription.activeSubscriptions} />
                <StatCard label="Expired" value={subscription.expiredSubscriptions} />
              </div>
            </div>
          )}

          {network && (
            <div className="mb-6">
              <p className="text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-3">
                Referral Network
              </p>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
                <StatCard label="Role" value={network.role} />
                <StatCard label="Elite" value={network.isElite ? 'Yes' : 'No'} />
                <StatCard label="Max Levels" value={network.maxLevels} />
                <StatCard label="Total Referrals" value={network.totalReferrals} />
                <StatCard label="Total Commission" value={network.totalCommission} />
              </div>
              {network.productTotals && Object.keys(network.productTotals).length > 0 && (
                <div className="mb-4">
                  <p className="text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-3">
                    Product Totals
                  </p>
                  <div className="overflow-x-auto">
                    <table className="w-full border-separate border-spacing-0">
                      <thead>
                        <tr>
                          <th className={thCls}>Product</th>
                          <th className={thCls}>Total Income</th>
                          <th className={thCls}>Total Sale</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Object.entries(network.productTotals).map(([product, data]) => (
                          <tr key={product} className="group hover:bg-white/[0.02] transition-colors">
                            <td className={tdCls}>{product}</td>
                            <td className={`${tdCls} font-mono`}>{data.totalIncome}</td>
                            <td className={`${tdCls} font-mono`}>{data.totalSale}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              {network.isElite && network.eliteIncome && (
                <div className="mb-4">
                  <p className="text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-3">
                    Elite Income
                  </p>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <StatCard label="Total Earnings" value={network.eliteIncome.totalEarnings} />
                  </div>
                </div>
              )}
              {Array.isArray(network.levels) && network.levels.length > 0 && (
                <div className="overflow-x-auto">
                  <table className="w-full border-separate border-spacing-0">
                    <thead>
                      <tr>
                        <th className={thCls}>Level</th>
                        <th className={thCls}>Total Users</th>
                        <th className={thCls}>Earnings</th>
                      </tr>
                    </thead>
                    <tbody>
                      {network.levels.map((lvl) => (
                        <tr key={lvl.level} className="group hover:bg-white/[0.02] transition-colors">
                          <td className={`${tdCls} font-mono`}>Level {lvl.level}</td>
                          <td className={`${tdCls} font-mono`}>{lvl.totalUsers}</td>
                          <td className={`${tdCls} font-mono`}>{lvl.earnings}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default UserCreditStats;
